import React, { useEffect, useCallback, memo, useMemo } from 'react';
import { Navbar, Nav, NavDropdown } from 'react-bootstrap';
import { IconBaseProps } from 'react-icons';
import { FaHome } from 'react-icons/fa';
import { useLocation, Link, useHistory } from 'react-router-dom';
import { useEntity } from '../../contexts/EntityContext';
import { useYear, Year } from '../../contexts/YearContext';
import { useArea } from '../../contexts/AreaContext';
import './SecondaryNavBar.css';

const SecondaryNavBar: React.FC = () => {
    const location = useLocation();
    const history = useHistory();
    const { currentEntity, setCurrentEntity, availableEntities, fetchAvailableEntities } = useEntity();
    const { currentYear, availableYears, setCurrentYear, clearCurrentYear } = useYear();
    const { currentArea, availableAreas, setCurrentArea, clearCurrentArea, fetchAvailableAreas } = useArea();

    useEffect(() => {
        fetchAvailableEntities();
    }, [fetchAvailableEntities]);

    useEffect(() => {
        if (currentEntity) {
            fetchAvailableAreas(currentEntity.entidade_id);
        }
    }, [currentEntity, fetchAvailableAreas]);

    const handleEntitySelect = useCallback(async (entityId: string) => {
        const entity = availableEntities.find(e => e.entidade_id === entityId);
        if (entity && entity.entidade_id !== currentEntity?.entidade_id) {
            clearCurrentYear();
            await setCurrentEntity(entity);
            history.push('/home');
        }
    }, [availableEntities, currentEntity, setCurrentEntity, clearCurrentYear, history]);

    const handleHomeClick = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        clearCurrentYear();
        clearCurrentArea();
        setCurrentEntity(null);
        history.push('/home');
    }, [setCurrentEntity, clearCurrentYear, clearCurrentArea, history]);

    const handleChangeEntity = useCallback(() => {
        clearCurrentYear();
        clearCurrentArea();
        setCurrentEntity(null);
        history.push('/home');
    }, [setCurrentEntity, clearCurrentYear, clearCurrentArea, history]);

    const handleChangeYear = useCallback(async (year: Year) => {
        if (year.ano_fiscal_id !== currentYear?.ano_fiscal_id) {
            await setCurrentYear(year);
        }
    }, [currentYear, setCurrentYear]);

    const handleAreaSelect = useCallback(async (areaId: string) => {
        const area = availableAreas.find(a => a.area_id === areaId);
        if (area && area.area_id !== currentArea?.area_id) {
            await setCurrentArea(area);
        }
    }, [availableAreas, currentArea, setCurrentArea]);

    const getFormattedPath = useCallback(() => {
        const path = location.pathname.split('/').filter(Boolean);

        if (path[0]?.toLowerCase() === 'ano') {
            const returnPath = location.state?.returnPath;
            if (returnPath) {
                const returnPathSegments = returnPath.split('/').filter(Boolean);
                if (returnPathSegments[0] === 'acoes' && returnPathSegments[1]) {
                    const segment = returnPathSegments[1];
                    // Try to find the area in availableAreas first
                    const area = availableAreas.find(a =>
                        a.area_id.toLowerCase() === segment.toLowerCase() ||
                        a.nome.toLowerCase().replace(/\s+/g, '').replace(/[çã]/g, 'c').replace(/[áàâ]/g, 'a').replace(/[éèê]/g, 'e').replace(/[íìî]/g, 'i').replace(/[óòô]/g, 'o').replace(/[úùû]/g, 'u') === segment.toLowerCase()
                    );
                    if (area) {
                        return area.nome;
                    }

                    // Fallback for special cases
                    switch (segment.toLowerCase()) {
                        case 'dashboard':
                            return 'Visualizador Geral';
                        default:
                            return segment.charAt(0).toUpperCase() + segment.slice(1);
                    }
                }
            }
            return '';
        }

        if (path.length < 2) return '';
        const segment = path[1];

        // Try to find the area in availableAreas first
        const area = availableAreas.find(a =>
            a.area_id.toLowerCase() === segment.toLowerCase() ||
            a.nome.toLowerCase().replace(/\s+/g, '').replace(/[çã]/g, 'c').replace(/[áàâ]/g, 'a').replace(/[éèê]/g, 'e').replace(/[íìî]/g, 'i').replace(/[óòô]/g, 'o').replace(/[úùû]/g, 'u') === segment.toLowerCase()
        );
        if (area) {
            return area.nome;
        }

        // Fallback for special cases
        switch (segment.toLowerCase()) {
            case 'dashboard':
                return 'Visualizador Geral';
            default:
                return segment.charAt(0).toUpperCase() + segment.slice(1);
        }
    }, [location.pathname, location.state, availableAreas]);

    const currentPath = useMemo(() => getFormattedPath(), [getFormattedPath]);

    const navigationItems = useMemo(() => {
        const items = [
            { path: 'dashboard', label: 'VISUALIZADOR GERAL' }
        ];

        // Add dynamic areas from availableAreas
        if (availableAreas && availableAreas.length > 0) {
            availableAreas.forEach(area => {
                // Convert area name to URL-friendly path
                const path = area.nome.toLowerCase()
                    .replace(/\s+/g, '')
                    .replace(/[çã]/g, 'c')
                    .replace(/[áàâ]/g, 'a')
                    .replace(/[éèê]/g, 'e')
                    .replace(/[íìî]/g, 'i')
                    .replace(/[óòô]/g, 'o')
                    .replace(/[úùû]/g, 'u');

                items.push({
                    path: path,
                    label: area.nome.toUpperCase()
                });
            });
        }

        return items;
    }, [availableAreas]);

    const handleAcoesNavigation = useCallback((path: string) => {
        if (!currentEntity) {
            handleChangeEntity();
            return;
        }
        history.push(`/acoes/${path.toLowerCase()}`);
    }, [currentEntity, handleChangeEntity, history]);

    const renderPathNavigation = useCallback(() => {
        if (!currentPath) return null;

        const pathSegments = location.pathname.split('/').filter(Boolean);
        const isInAcoesSection = pathSegments[0]?.toLowerCase() === 'acoes';
        const isInYearSelection = pathSegments[0]?.toLowerCase() === 'ano';

        // Don't show path navigation if we have a current area (area dropdown will handle it)
        if (currentArea) return null;

        if (isInAcoesSection || isInYearSelection) {
            if (navigationItems.length === 0) {
                return null;
            }

            return (
                <NavDropdown
                    title={currentPath}
                    id="progov-dropdown"
                    className="ms-3 fw-bold"
                >
                    {navigationItems.map(({ path, label }) => (
                        <NavDropdown.Item
                            key={path}
                            onClick={() => handleAcoesNavigation(path)}
                        >
                            {label}
                        </NavDropdown.Item>
                    ))}
                </NavDropdown>
            );
        }

        return null;
    }, [currentPath, location.pathname, handleAcoesNavigation, navigationItems, currentArea]);

    const renderEntityDropdown = useMemo(() => {
        if (!currentEntity) return null;

        return (
            <NavDropdown
                title={currentEntity.nome}
                id="entity-dropdown"
                className="fw-bold me-3 entity-dropdown"
            >
                {availableEntities.map((entity) => (
                    <NavDropdown.Item
                        key={entity.entidade_id}
                        onClick={() => handleEntitySelect(entity.entidade_id)}
                        active={entity.entidade_id === currentEntity.entidade_id}
                    >
                        {entity.nome}
                    </NavDropdown.Item>
                ))}
            </NavDropdown>
        );
    }, [currentEntity, availableEntities, handleEntitySelect]);

    const renderAreaDropdown = useMemo(() => {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        const isInAcoesSection = pathSegments[0]?.toLowerCase() === 'acoes';
        const isInYearSelection = pathSegments[0]?.toLowerCase() === 'ano';

        if (!currentArea || (!isInAcoesSection && !isInYearSelection) || availableAreas.length === 0) return null;

        return (
            <NavDropdown
                title={currentArea.nome}
                id="area-dropdown"
                className="fw-bold ms-3 area-dropdown"
            >
                {availableAreas.map((area) => (
                    <NavDropdown.Item
                        key={area.area_id}
                        onClick={() => handleAreaSelect(area.area_id)}
                        active={area.area_id === currentArea.area_id}
                    >
                        {area.nome}
                    </NavDropdown.Item>
                ))}
            </NavDropdown>
        );
    }, [currentArea, availableAreas, handleAreaSelect, location.pathname]);

    const renderYearDropdown = useMemo(() => {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        const isInAcoesSection = pathSegments[0]?.toLowerCase() === 'acoes';
        const isInYearSelection = pathSegments[0]?.toLowerCase() === 'ano';

        if (!currentYear || (!isInAcoesSection && !isInYearSelection)) return null;

        return (
            <NavDropdown
                title={currentYear.ano.toString()}
                id="year-dropdown"
                className="fw-bold ms-3 year-dropdown"
            >
                {availableYears.map((year) => (
                    <NavDropdown.Item
                        key={year.ano_fiscal_id}
                        onClick={() => handleChangeYear(year)}
                        active={year.ano_fiscal_id === currentYear.ano_fiscal_id}
                    >
                        {year.ano.toString()}
                    </NavDropdown.Item>
                ))}
            </NavDropdown>
        );
    }, [currentYear, availableYears, handleChangeYear, location.pathname]);

    return (
        <Navbar bg="light" className="secondary-navbar border-bottom">
            <div className="container-fluid">
                <Nav className="align-items-center">
                    <Nav.Item>
                        <Link to="/" className="nav-link p-0 me-2" onClick={handleHomeClick}>
                            {React.createElement(FaHome as React.ComponentType<IconBaseProps>, {
                                className: "home-icon",
                                size: 20,
                                title: "Voltar para seleção de entidade"
                            })}
                        </Link>
                    </Nav.Item>
                    {currentEntity && (
                        <>
                            {renderEntityDropdown}
                        </>
                    )}
                    {renderAreaDropdown}
                    {renderPathNavigation()}
                    {renderYearDropdown}
                </Nav>
            </div>
        </Navbar>
    );
};

export default memo(SecondaryNavBar); 